import { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export enum ConnectionState {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  RECONNECTING = 'reconnecting',
}

export interface TaxonomyStatus {
  run_id: string;
  status: string;
  progress: number;
  step?: string;
  total_chunks_processed: number;
  chunks_in_step?: number;
  intents_discovered: number;
  created_at: string;
  completed_at: string | null;
  error_log?: string;
}

export interface DocumentStatus {
  document_id: string;
  name: string;
  status: string;
  progress: number;
  progress_msg: string;
  chunk_count?: number;
  token_count?: number;
  error?: string;
  completed?: boolean;
}

export interface DocumentsSummary {
  total: number;
  processing: number;
  completed: number;
  failed: number;
  pending: number;
}

export interface Notification {
  title: string;
  message: string;
  level: 'info' | 'warning' | 'error' | 'success';
  timestamp: string;
}

interface KnowledgeBaseWebSocketResult {
  connectionState: ConnectionState;
  taxonomyStatus: TaxonomyStatus | null;
  documentsSummary: DocumentsSummary | null;
  lastDocumentUpdate: DocumentStatus | null;
  lastNotification: Notification | null;
  error: string | null;
  send: (data: any) => boolean;
  clearTaxonomyStatus: () => void;
}

/**
 * App-level WebSocket hook for all real-time updates within a knowledge base.
 * 
 * This single connection handles:
 * - Taxonomy pipeline status updates
 * - Document processing status updates
 * - General notifications
 * 
 * The connection stays alive regardless of task success/failure.
 */
export function useKnowledgeBaseWebSocket(kbId: string): KnowledgeBaseWebSocketResult {
  const [connectionState, setConnectionState] = useState<ConnectionState>(ConnectionState.DISCONNECTED);
  const [taxonomyStatus, setTaxonomyStatus] = useState<TaxonomyStatus | null>(null);
  const [documentsSummary, setDocumentsSummary] = useState<DocumentsSummary | null>(null);
  const [lastDocumentUpdate, setLastDocumentUpdate] = useState<DocumentStatus | null>(null);
  const [lastNotification, setLastNotification] = useState<Notification | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const heartbeatIntervalRef = useRef<number | null>(null);
  const queryClient = useQueryClient();

  const MAX_RECONNECT_ATTEMPTS = 10;
  const BASE_RECONNECT_DELAY = 1000;
  const MAX_RECONNECT_DELAY = 30000;
  const HEARTBEAT_INTERVAL = 30000;

  const cleanup = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (socketRef.current) {
      socketRef.current.onopen = null;
      socketRef.current.onmessage = null;
      socketRef.current.onclose = null;
      socketRef.current.onerror = null;
      if (socketRef.current.readyState === WebSocket.OPEN || 
          socketRef.current.readyState === WebSocket.CONNECTING) {
        socketRef.current.close();
      }
      socketRef.current = null;
    }
  }, []);

  const send = useCallback((data: any): boolean => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      console.warn('[KB WebSocket] Cannot send: not connected');
      return false;
    }
    try {
      const message = typeof data === 'string' ? data : JSON.stringify(data);
      socketRef.current.send(message);
      return true;
    } catch (err) {
      console.error('[KB WebSocket] Send error:', err);
      return false;
    }
  }, []);

  const clearTaxonomyStatus = useCallback(() => {
    setTaxonomyStatus(null);
  }, []);

  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    heartbeatIntervalRef.current = window.setInterval(() => {
      send({ type: 'ping', timestamp: Date.now() });
    }, HEARTBEAT_INTERVAL);
  }, [send]);

  const scheduleReconnect = useCallback(() => {
    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.warn('[KB WebSocket] Max reconnect attempts reached');
      setError('Connection lost. Please refresh the page.');
      return;
    }

    reconnectAttemptsRef.current++;
    const delay = Math.min(
      BASE_RECONNECT_DELAY * Math.pow(1.5, reconnectAttemptsRef.current - 1),
      MAX_RECONNECT_DELAY
    ) * (0.8 + Math.random() * 0.4);

    console.log(`[KB WebSocket] Reconnecting in ${Math.round(delay)}ms (attempt ${reconnectAttemptsRef.current})`);
    setConnectionState(ConnectionState.RECONNECTING);

    reconnectTimeoutRef.current = window.setTimeout(() => {
      reconnectTimeoutRef.current = null;
      connect();
    }, delay);
  }, []);

  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data);
      const { type, data } = message;

      console.log('[KB WebSocket] Received:', type, data);

      switch (type) {
        case 'connected':
          // Initial state received on connection
          if (data?.initial_state) {
            if (data.initial_state.taxonomy) {
              setTaxonomyStatus(data.initial_state.taxonomy);
            }
            if (data.initial_state.documents) {
              setDocumentsSummary(data.initial_state.documents);
            }
          }
          break;

        case 'taxonomy_status':
          setTaxonomyStatus(data);
          // Invalidate queries when status changes to completed or failed
          if (data?.status === 'completed' || data?.status === 'failed') {
            queryClient.invalidateQueries({ queryKey: ['taxonomy-latest', kbId] });
            if (data?.run_id) {
              queryClient.invalidateQueries({ queryKey: ['taxonomy-result', kbId, data.run_id] });
              queryClient.invalidateQueries({ queryKey: ['taxonomy-graph', kbId, data.run_id] });
            }
          }
          break;

        case 'document_status':
          setLastDocumentUpdate(data);
          // Update documents summary if we have counts
          if (data?.completed || data?.status === 'DONE' || data?.status === 'FAIL') {
            queryClient.invalidateQueries({ queryKey: ['documents', kbId] });
          }
          break;

        case 'reconciliation_update':
          // Invalidate reconciliation queries on any update
          if (data?.reconciliation_id) {
            queryClient.invalidateQueries({ queryKey: ['reconciliation-status', kbId, data.reconciliation_id] });
          }
          if (data?.status === 'pending_review' || data?.status === 'failed') {
            queryClient.invalidateQueries({ queryKey: ['reconciliation-latest', kbId] });
          }
          break;

        case 'notification':
          setLastNotification(data);
          break;

        case 'pong':
          // Heartbeat response, connection is alive
          break;

        default:
          console.log('[KB WebSocket] Unknown message type:', type);
      }
    } catch (err) {
      console.error('[KB WebSocket] Message parse error:', err);
    }
  }, [kbId, queryClient]);

  const connect = useCallback(() => {
    if (!kbId) return;

    cleanup();
    setConnectionState(ConnectionState.CONNECTING);
    setError(null);

    // Use VITE_WS_URL if set, otherwise fall back to same-origin WebSocket
    const wsBaseUrl = import.meta.env.VITE_WS_URL;
    let wsUrl: string;
    
    if (wsBaseUrl) {
      // Production: use configured WebSocket URL (e.g., wss://ws.example.com)
      wsUrl = `${wsBaseUrl}/ws/kb/${kbId}/`;
    } else {
      // Development: use same-origin WebSocket
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.host;
      wsUrl = `${protocol}//${host}/ws/kb/${kbId}/`;
    }

    console.log('[KB WebSocket] Connecting to:', wsUrl);

    try {
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log('[KB WebSocket] Connected');
        reconnectAttemptsRef.current = 0;
        setConnectionState(ConnectionState.CONNECTED);
        setError(null);
        startHeartbeat();
      };

      socket.onmessage = handleMessage;

      socket.onclose = (event) => {
        console.log('[KB WebSocket] Closed:', event.code, event.reason);
        cleanup();
        setConnectionState(ConnectionState.DISCONNECTED);
        
        // Don't reconnect on normal closure or auth errors
        if (event.code === 1000 || event.code === 4003 || event.code === 4004) {
          if (event.code === 4003) {
            setError('Authentication required');
          } else if (event.code === 4004) {
            setError('Access denied to this knowledge base');
          }
          return;
        }
        
        // Schedule reconnect for abnormal closures
        scheduleReconnect();
      };

      socket.onerror = (event) => {
        console.error('[KB WebSocket] Error:', event);
        // onclose will be called after onerror, so we don't need to handle reconnect here
      };
    } catch (err) {
      console.error('[KB WebSocket] Connection error:', err);
      setConnectionState(ConnectionState.DISCONNECTED);
      scheduleReconnect();
    }
  }, [kbId, cleanup, handleMessage, startHeartbeat, scheduleReconnect]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    if (kbId) {
      connect();
    }
    return () => {
      cleanup();
    };
  }, [kbId, connect, cleanup]);

  return {
    connectionState,
    taxonomyStatus,
    documentsSummary,
    lastDocumentUpdate,
    lastNotification,
    error,
    send,
    clearTaxonomyStatus,
  };
}

export default useKnowledgeBaseWebSocket;
